import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface TransitionConfig {
  type: string;
  duration: number;
  easing: string;
}

interface PageTransitionProps {
  children: React.ReactNode;
}

// Cache global settings
let cachedGlobal: TransitionConfig | null = null;

async function loadGlobalTransition(): Promise<TransitionConfig> {
  if (cachedGlobal) return cachedGlobal;
  const { data } = await supabase
    .from('site_settings')
    .select('page_transition_type, page_transition_duration, page_transition_easing')
    .limit(1)
    .maybeSingle();
  cachedGlobal = {
    type: (data as any)?.page_transition_type || 'none',
    duration: (data as any)?.page_transition_duration || 600,
    easing: (data as any)?.page_transition_easing || 'ease',
  };
  return cachedGlobal;
}

// Invalidate cache when settings change
export function invalidateTransitionCache() {
  cachedGlobal = null;
}

function getTransitionStyles(type: string, phase: 'enter' | 'exit', duration: number, easing: string): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    transitionProperty: 'opacity, transform',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: easing,
  };

  switch (type) {
    case 'fade':
      return { ...base, opacity: phase === 'enter' ? 0 : 1 };
    case 'slide-left':
      return { ...base, opacity: phase === 'enter' ? 0 : 1, transform: phase === 'enter' ? 'translateX(100vw)' : 'translateX(0)' };
    case 'slide-right':
      return { ...base, opacity: phase === 'enter' ? 0 : 1, transform: phase === 'enter' ? 'translateX(-100vw)' : 'translateX(0)' };
    case 'slide-up':
      return { ...base, opacity: phase === 'enter' ? 0 : 1, transform: phase === 'enter' ? 'translateY(100vh)' : 'translateY(0)' };
    case 'slide-down':
      return { ...base, opacity: phase === 'enter' ? 0 : 1, transform: phase === 'enter' ? 'translateY(-100vh)' : 'translateY(0)' };
    case 'scale':
      return { ...base, opacity: phase === 'enter' ? 0 : 1, transform: phase === 'enter' ? 'scale(0.95)' : 'scale(1)' };
    default:
      return { ...base, opacity: 1 };
  }
}

function getExitStyles(type: string, duration: number, easing: string): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    transitionProperty: 'opacity, transform',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: easing,
  };

  switch (type) {
    case 'fade':
      return { ...base, opacity: 0 };
    case 'slide-left':
      return { ...base, opacity: 0, transform: 'translateX(-100vw)' };
    case 'slide-right':
      return { ...base, opacity: 0, transform: 'translateX(100vw)' };
    case 'slide-up':
      return { ...base, opacity: 0, transform: 'translateY(-100vh)' };
    case 'slide-down':
      return { ...base, opacity: 0, transform: 'translateY(100vh)' };
    case 'scale':
      return { ...base, opacity: 0, transform: 'scale(0.95)' };
    default:
      return { ...base, opacity: 0 };
  }
}

export function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const [displayedChildren, setDisplayedChildren] = useState(children);
  const [phase, setPhase] = useState<'idle' | 'exiting' | 'entering'>('idle');
  const [config, setConfig] = useState<TransitionConfig>({ type: 'none', duration: 600, easing: 'ease' });
  const prevKeyRef = useRef(location.key);
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingChildrenRef = useRef<React.ReactNode>(null);

  // Load global config once
  useEffect(() => {
    loadGlobalTransition().then(setConfig);
  }, []);

  // Handle route changes
  useEffect(() => {
    if (location.key === prevKeyRef.current) return;
    prevKeyRef.current = location.key;

    if (config.type === 'none') {
      setDisplayedChildren(children);
      window.scrollTo(0, 0);
      return;
    }

    // Start exit
    pendingChildrenRef.current = children;
    setPhase('exiting');

    const exitTimer = setTimeout(() => {
      setDisplayedChildren(pendingChildrenRef.current);
      setPhase('entering');
      window.scrollTo(0, 0);

      const enterTimer = setTimeout(() => {
        setPhase('idle');
      }, config.duration);

      return () => clearTimeout(enterTimer);
    }, config.duration);

    return () => clearTimeout(exitTimer);
  }, [location.key, children, config]);

  // No transition
  if (config.type === 'none') {
    return <>{children}</>;
  }

  const getContainerStyle = (): React.CSSProperties => {
    if (phase === 'idle') {
      return {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      };
    }

    if (phase === 'exiting') {
      return {
        ...getExitStyles(config.type, config.duration, config.easing),
        position: 'relative',
        overflow: 'hidden',
      };
    }

    // entering
    return {
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      opacity: 1,
      transform: 'none',
      transitionProperty: 'opacity, transform',
      transitionDuration: `${config.duration}ms`,
      transitionTimingFunction: config.easing,
    };
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div style={getContainerStyle()}>
        {displayedChildren}
      </div>
    </div>
  );
}
